import { useRef, useState } from "react";
import type { Book } from "@shared/types";
import { createBook, scanBook } from "../api";
import { ApiError } from "../api";
import { BookConfirm } from "./BookConfirm";

export function ScanBookTab() {
  const [input, setInput] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<Book | null>(null);
  const [savedTitle, setSavedTitle] = useState<string | null>(null);

  // Scanner-vs-typing detection: handheld scanners blast keystrokes faster than
  // any human. If we see >=5 events within 100ms each, treat it as a scanner
  // and auto-submit ISBN-10 too. Manual typing waits for ISBN-13 only.
  const lastKeystroke = useRef(0);
  const rapidCount = useRef(0);
  const debounceTimer = useRef<number | null>(null);

  const submit = async (raw: string) => {
    const isbn = raw.replace(/[-\s]/g, "");
    if (!isbn) return;
    // Guard against duplicate submits from scanners that append Enter
    // immediately after the 13-digit auto-submit has already fired.
    if (pending) return;
    if (debounceTimer.current) window.clearTimeout(debounceTimer.current);
    setPending(true);
    setError(null);
    try {
      const r = await scanBook(isbn);
      setResult(r.book);
      setInput("");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Scan failed");
    } finally {
      setPending(false);
    }
  };

  const onManualSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    submit(input);
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInput(val);
    setSavedTitle(null);

    const now = Date.now();
    if (now - lastKeystroke.current < 100) rapidCount.current++;
    else rapidCount.current = 0;
    lastKeystroke.current = now;
    const isScanner = rapidCount.current >= 5;

    const digits = val.replace(/[-\s]/g, "");
    if (debounceTimer.current) window.clearTimeout(debounceTimer.current);

    if (/^\d{13}$/.test(digits)) {
      submit(digits);
    } else if (/^\d{9}[\dXx]$/.test(digits) && isScanner) {
      // ISBN-10 only auto-submits if we're confident it's a scanner.
      debounceTimer.current = window.setTimeout(() => submit(digits), 300);
    }
  };

  const onSave = async (book: Book) => {
    try {
      const saved = await createBook(book);
      setResult(null);
      setSavedTitle(saved.title);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Save failed");
    }
  };

  if (result) {
    return (
      <BookConfirm
        book={result}
        onSave={onSave}
        onCancel={() => setResult(null)}
      />
    );
  }

  return (
    <form onSubmit={onManualSubmit} className="space-y-4">
      <label className="block text-sm" htmlFor="isbn">
        Scan or enter ISBN
      </label>
      <div className="flex gap-2">
        <input
          id="isbn"
          type="text"
          inputMode="numeric"
          autoFocus
          autoComplete="off"
          value={input}
          onChange={onChange}
          placeholder="978…"
          className="flex-1 px-4 py-3 text-lg bg-gray-800 border border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <button
          type="submit"
          disabled={pending || !input.trim()}
          className="px-4 py-3 bg-blue-600 text-white rounded font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Look Up
        </button>
      </div>
      {pending && <p className="text-sm text-gray-400">Looking up…</p>}
      {error && (
        <div role="alert" className="text-sm text-red-400">
          {error}
        </div>
      )}
      {savedTitle && (
        <div className="text-sm text-green-400">Saved: {savedTitle}</div>
      )}
      <p className="text-xs text-gray-500">
        Handheld scanners auto-submit ISBN-13s. For ISBN-10 or manual entry,
        press Enter or tap Look Up.
      </p>
    </form>
  );
}
